// src/components/AuthForm.jsx
import React, { useState, useContext, useEffect, useTransition } from "react";
import {
	Box,
	TextField,
	FormControl,
	FormLabel,
	FormGroup,
	Typography,
	Link,
	Button,
	Divider,
	IconButton,
} from "@mui/material";
import { useTheme } from '@mui/material/styles';
import sondaLogo from "../../images/sonda-screener.svg";
import PropTypes from "prop-types";
import { AuthenticationDetails, CognitoUser } from "amazon-cognito-identity-js";
import { useCognitoContext } from "../../context/CognitoContext";
import { useAuthContext } from "../../context/AuthContext";
import { useSnackbar } from "../../context/SnackbarContext";
import { authenticateServer } from "../../utils/authUtils";
import PasswordCondition from "../common/BasicSnackbar/PasswordCondition";
import LanguageSelector from "../LanguageSelector";
import { useTranslation } from "../../context/TranslationContext";
import PasswordConditionsList from "./PasswordConditionList";
import { StyledCloseIcon, AuthContainer, StyledFormGroup, StyledFormControl, StyledTypography, StyledAuthError } from "../../constants/styledComponents";
import { validateEmail, validatePassword, checkPasswordConditions } from "../../utils/authFormValidation";
import { TestWorkflowAction } from "../../constants/testWorkflowState";
import { useAppWorkflowContext } from "../../context/AppWorkflowContext";

const AuthForm = ({
	showLogo = true,
	usernameLabel = "Email",
	passwordLabel = "authForm.fieldLabels.passwordLabel",
	newPasswordLabel = "authForm.fieldLabels.newPassword",
	confirmPasswordLabel = "authForm.fieldLabels.confirmPassword",
	verificationCodeLabel = "authForm.fieldLabels.verificationCode",
	onLogoClick = () => { },
	onClose = () => {},
	buttonText,
	children,
}) => {
	const { translate } = useTranslation();
	
	const usernameInputProps = { type: "email", placeholder: "your@email.com" };
	const passwordInputProps = { type: "password", placeholder: translate(passwordLabel) };
	const newPasswordInputProps = { type: "password", placeholder: translate(newPasswordLabel) };
	const confirmPasswordInputProps = { type: "password", placeholder: translate(confirmPasswordLabel) };
	const verificationCodeInputProps = { type: "password", placeholder: translate(verificationCodeLabel) };
	
	const [username, setUsername] = useState("");
	const [password, setPassword] = useState("");
	const [passwordConfirm, setPasswordConfirm] = useState("");
	const [verificationCode, setVerificationCode] = useState("");
	const [usernameError, setUsernameError] = useState("");
	const [passwordError, setPasswordError] = useState("");
	const [newPasswordError, setNewPasswordError] = useState("");
	const [passwordConfirmError, setPasswordConfirmError] = useState("");
	const [verificationCodeError, setVerificationCodeError] = useState("");
	const [userDetails, setUserDetails] = useState(null);
	const [state, setState] = useState(STATE_LOGIN);
	const [errorMessage, setErrorMessage] = useState("");
	const { openSnackbar } = useSnackbar();
	const { setUserData, setIsAuthenticated } = useAuthContext();
	const { userPool, signOut } = useCognitoContext();
	const theme = useTheme();
	const [isPasswordFocused, setPasswordFocused] = useState(false);

	const conditions = checkPasswordConditions(password);
	const { dispatchWorkflow } = useAppWorkflowContext();

	const flushPasswords = () => {
		setPasswordConfirm("");
		setPassword("");
	};

	useEffect(() => {
		if (errorMessage) {
			setErrorMessage("");
			openSnackbar("error", errorMessage);
		}
	}, [errorMessage, openSnackbar]);

	const usernameErr = validateEmail(username, translate);
	const passwordErr = validatePassword(password, state, translate);

	const handleSubmit = (event) => {
		event.preventDefault();
		setErrorMessage("");

		setUsernameError(usernameErr);
		setPasswordError(passwordErr);
		if (state === STATE_FORGOT_PASSWORD && usernameErr) {
			return;
		}

		if (state === STATE_FORGOT_PASSWORD_CHANGE) {
			setNewPasswordError(passwordErr);
		}

		const authenticationData = { Username: username, Password: password };
		const authDetails = new AuthenticationDetails(authenticationData);
		const userData = { Username: username, Pool: userPool };
		const cognitoUser = new CognitoUser(userData);

		switch (state) {
			case STATE_LOGIN:
				cognitoUser.authenticateUser(authDetails, {
					onSuccess(result) {
						if (result) {
							authenticateServer(result.getIdToken().getJwtToken())
								.then((data) => {
									flushPasswords();
									setIsAuthenticated(true);
									localStorage.setItem("userEmail", username);
									localStorage.setItem("isBackendAuthenticated", "true");
									setUserData((prevData) => ({
										...prevData,
										email: username,
										...data,
									}));
									dispatchWorkflow({ type: TestWorkflowAction.ENTER_APP_INIT });
								})
								.catch((error) => {
									if (error.response && error.response.data && error.response.data.message) {
										const statusCode = error.response.status;
										const serverError = error.response.data.message;
										let errorMessage = translate("authForm.serverError");

										switch (statusCode) {
											case 400:
												errorMessage = translate("authForm.badRequest");
												break;
											case 401:
												errorMessage = translate("authForm.unauthorized");
												break;
											case 404:
												errorMessage = `${serverError} ${translate("authForm.notFound")}`;
												break;
											case 503:
												errorMessage = serverError;
												break;
											default:
												errorMessage = `${translate("authForm.errorPrefix")} ${statusCode}: ${translate("authForm.generalError")}`;
										}
										setErrorMessage(errorMessage);
									} else if (error.request) {
										console.error("No Response:", error.request);
										setErrorMessage(translate("authForm.noResponse"));
									} else {
										console.error("Request Error:", error.message);
										setErrorMessage(`${translate("authForm.requestError")} ${error.message}`);
									}

									signOut();
								});
						}
					},
					onFailure(err) {
						console.log(err);
						if (err.code === "UserNotFoundException" || err.code === "NotAuthorizedException") {
							setErrorMessage(translate("authForm.invalidCredentials"));
						} else if (err.code === "PasswordResetRequiredException") {
							setErrorMessage(translate("authForm.passwordResetRequired"));
						}
					},
					newPasswordRequired(userAttributes) {
						delete userAttributes.email_verified;
						delete userAttributes.email;
						setUserDetails({ user: cognitoUser, userAttr: userAttributes });
						flushPasswords();
						setState(STATE_PASSWORD_CHANGE);
					},
				});
				break;
			case STATE_FORGOT_PASSWORD:
				cognitoUser.forgotPassword({
					onSuccess(result) {
						flushPasswords();
						setState(STATE_FORGOT_PASSWORD_CHANGE);
					},
					onFailure(err) {
						console.log(err);
						if (err.code === "LimitExceededException") {
							setUsernameError(translate("authForm.attemptLimitExceeded"));
						} else {
							setUsernameError(translate("authForm.failedToInitiatePasswordReset"));
						}
					},
				});
				break;
			case STATE_FORGOT_PASSWORD_CHANGE:
				if (!verificationCode) {
					setVerificationCodeError("Verification code is required.");
					return
				}
				setVerificationCodeError("");
				if (password === passwordConfirm) {
					if (passwordErr) {
						return;
					}
					cognitoUser.confirmPassword(verificationCode, password, {
						onSuccess() {
							flushPasswords();
							setState(STATE_LOGIN);
							setVerificationCodeError("");

							openSnackbar("success", translate("authForm.passwordChangedSuccessfully"));
						},
						onFailure(err) {
							console.log(err);
							if (err.code === "CodeMismatchException") {
								setVerificationCodeError(translate("authForm.invalidVerificationCode"));
							} else if (err.code === "LimitExceededException") {
								setVerificationCodeError(translate("authForm.tooManyAttempts"));
							} else {
								setVerificationCodeError(translate("authForm.failedToVerifyCode"));
							}
						},
					});
				} else {
					setPasswordConfirmError(translate("authForm.confirmPasswordError"));
				}
				break;
			case STATE_PASSWORD_CHANGE:
				if (password === passwordConfirm) {
					if (passwordErr) {
						return;
					}
					userDetails.user.completeNewPasswordChallenge(password, userDetails.userAttr, {
						onSuccess(result) {
							if (result) {
								flushPasswords();
								setState(STATE_LOGIN);
							}
						},
						onFailure(err) {
							console.log(err);
						},
					});
				} else {
					setPasswordConfirmError(translate("authForm.confirmPasswordError"));
				}
				break;
			default:
				break;
		}
	};

	const renderButtonText = () => {
		if (buttonText) return buttonText;

		switch (state) {
			case STATE_PASSWORD_CHANGE:
			case STATE_FORGOT_PASSWORD_CHANGE:
				return translate("authForm.resetPasswordButton");
			case STATE_FORGOT_PASSWORD:
				return translate("authForm.resetPasswordButton");
			case STATE_LOGIN:
			default:
				return "Login";
		}
	};

	const renderForm = () => {
		switch (state) {
			case STATE_PASSWORD_CHANGE:
				return (
					<AuthContainer>
						<Typography variant="h6">{translate("authForm.selectNewPassword")}</Typography>
						<StyledFormGroup>
							<StyledFormControl fullWidth>
								<TextField
									label={translate(newPasswordLabel)}
									id={newPasswordLabel}
									{...newPasswordInputProps}
									value={password}
									onChange={(event) => {
										setPassword(event.target.value);
										setPasswordError("");
									}}
									onFocus={() => setPasswordFocused(true)}
									onBlur={() => {
										setPasswordFocused(false);
									}}
									error={!!passwordError}
									helperText={passwordError}
								/>
								<Box mt={2}>
									<Box mt={2}>
										<Box mt={2}>
											<PasswordConditionsList conditions={conditions} isFocused={isPasswordFocused} />
										</Box>
									</Box>
								</Box>
							</StyledFormControl>
						</StyledFormGroup>
						<StyledFormGroup>
							<StyledFormControl fullWidth>
								<TextField
									label={translate(confirmPasswordLabel)}
									id={confirmPasswordLabel}
									{...confirmPasswordInputProps}
									value={passwordConfirm}
									onChange={(event) => {
										setPasswordConfirm(event.target.value);
										setPasswordConfirmError("");
									}}
									error={!!passwordConfirmError}
									helperText={passwordConfirmError}
								/>
							</StyledFormControl>
						</StyledFormGroup>
					</AuthContainer>
				);
			case STATE_FORGOT_PASSWORD_CHANGE:
				return (
					<>
						<Typography style={{ marginBottom: "10px" }} variant="h6">
							{translate("authForm.selectNewPassword")}
						</Typography>
						<Typography
							style={{ marginBottom: "10px" }}
							variant="subtitle1"
							color="textSecondary"
							gutterBottom
						>
							{translate("authForm.verificationCodeSent")}
						</Typography>
						<StyledFormGroup>
							<TextField
								label={translate("authForm.fieldLabels.verificationCode")}
								id={verificationCodeLabel}
								{...verificationCodeInputProps}
								value={verificationCode}
								onChange={(event) => {
									setVerificationCode(event.target.value);
									setVerificationCodeError("");
								}}
								error={!!verificationCodeError}
								helperText={verificationCodeError}
							/>
						</StyledFormGroup>
						<StyledFormGroup>
							<StyledFormControl fullWidth>
								<TextField
									label={translate("authForm.fieldLabels.newPassword")}
									id={newPasswordLabel}
									{...newPasswordInputProps}
									value={password}
									onChange={(event) => {
										setPassword(event.target.value);
										setNewPasswordError("");
									}}
									onFocus={() => setPasswordFocused(true)}
									onBlur={() => {
										setPasswordFocused(false);
									}}
									error={!!newPasswordError}
									helperText={newPasswordError}
								/>
								<Box mt={2}>
									<Box mt={2}>
										<PasswordConditionsList conditions={conditions} isFocused={isPasswordFocused} />
									</Box>
								</Box>
							</StyledFormControl>
						</StyledFormGroup>
						<StyledFormGroup>
							<StyledFormControl fullWidth>
								<TextField
									label={translate("authForm.fieldLabels.confirmPassword")}
									id={confirmPasswordLabel}
									{...confirmPasswordInputProps}
									value={passwordConfirm}
									onChange={(event) => {
										setPasswordConfirm(event.target.value);
										setPasswordConfirmError("");
									}}
									error={!!passwordConfirmError}
									helperText={passwordConfirmError}
								/>
							</StyledFormControl>
						</StyledFormGroup>
					</>
				);
			case STATE_FORGOT_PASSWORD:
				return (
					<>
						<Typography style={{ marginBottom: "10px" }} variant="subtitle1">
							{translate("authForm.forgotPasswordMessage")}
						</Typography>

						<Box component="form" noValidate>
							<StyledFormGroup>

								<TextField
									label={usernameLabel}
									{...usernameInputProps}
									value={username}
									onChange={(event) => {
										setUsername(event.target.value);
										setUsernameError("");
									}}
									id={usernameLabel}
									error={!!usernameError}
									helperText={usernameError}
								/>
							</StyledFormGroup>
						</Box>

					</>
				);
			case STATE_LOGIN:
			default:
				return (
					<>
						<StyledTypography color="textPrimary">
							{translate("authForm.signInMessage")}
						</StyledTypography>
						<StyledFormGroup>
							<TextField
								label={usernameLabel}
								id={usernameLabel}
								{...usernameInputProps}
								value={username}
								onChange={(event) => {
									setUsername(event.target.value);
									setUsernameError("");
								}}
								error={!!usernameError}
								helperText={usernameError}
							/>
						</StyledFormGroup>
						<StyledFormGroup>
							<TextField
								label={translate(passwordLabel)}
								id={passwordLabel}
								{...passwordInputProps}
								value={password}
								onChange={(event) => {
									setPassword(event.target.value);
									setPasswordError("");
								}}
								type="password"
								error={!!passwordError}
								helperText={passwordError}
							/>
						</StyledFormGroup>
						<Box display="flex" justifyContent="flex-end">
							<Link
								href="#"
								color="primary"
								underline="hover"
								textAlign="right"
								onClick={() => {
									setState(STATE_FORGOT_PASSWORD);
								}}
							>
								{translate("authForm.forgotPassword")}
							</Link>
						</Box>
					</>
				);
		}
	};

	return (
		<AuthContainer>
			<Box display="flex" justifyContent="space-between" alignItems="center" width="100%" mb={2}>
				<LanguageSelector />
				{state === STATE_FORGOT_PASSWORD && (
					<IconButton
						onClick={() => {
							setState(STATE_LOGIN);
							onClose();
						}}
						aria-label="close"
						size="small"
						style={{ color: theme.palette.grey[500] }}
					>
						<StyledCloseIcon />
					</IconButton>
				)}
			</Box>
			<StyledFormControl component="form" onSubmit={handleSubmit}>
				{showLogo && (
					<Box display="flex" justifyContent="center" pb={4}>
						<IconButton
							onClick={onLogoClick}
							disableRipple
							sx={{ "&:hover, &:focus": { cursor: "default" } }}
						>
							<img src={sondaLogo} width={300} alt="logo" />
						</IconButton>
					</Box>
				)}
				<Box mb={2}>{renderForm()}</Box>
				{errorMessage && (
					<>
						<Divider />
						<StyledAuthError>{errorMessage}</StyledAuthError>
					</>
				)}
				<Divider />
				<TextField type="submit" style={{ display: "none" }} />
				<Button
					variant="contained"
					size="large"
					fullWidth
					onClick={handleSubmit}
					color="primary"
					sx={{ backgroundColor: theme.palette.primary.main }}
				>
					{renderButtonText()}
				</Button>
				<Box sx={{marginBottom: theme.spacing(3)}}>{children}</Box>
			</StyledFormControl>
		</AuthContainer>
	);
};

export const STATE_LOGIN = "LOGIN";
export const STATE_FORGOT_PASSWORD = "FORGOT_PASSWORD";
export const STATE_FORGOT_PASSWORD_CHANGE = "FORGOT_PASSWORD_CHANGE";
export const STATE_PASSWORD_CHANGE = "PASSWORD_CHANGE";

AuthForm.propTypes = {
	showLogo: PropTypes.bool,
	usernameLabel: PropTypes.string,
	usernameInputProps: PropTypes.object,
	passwordLabel: PropTypes.string,
	passwordInputProps: PropTypes.object,
	newPasswordLabel: PropTypes.string,
	newPasswordInputProps: PropTypes.object,
	confirmPasswordLabel: PropTypes.string,
	confirmPasswordInputProps: PropTypes.object,
	verificationCodeLabel: PropTypes.string,
	verificationCodeInputProps: PropTypes.object,
	onLogoClick: PropTypes.func,
	buttonText: PropTypes.string,
	children: PropTypes.node,
};

export default AuthForm;
